import { apiPlugin, ISbStoriesParams, storyblokInit } from '@storyblok/react/rsc';
import dynamic from 'next/dynamic';

import { env } from '@natu/env';

import { isDraftMode } from '../storyblok-preview';
import { relations } from './relations';
import { getSdk, Sdk, SdkFunctionWrapper } from './sdk';

export const getStoryblokApi = storyblokInit({
  accessToken: env.NEXT_PUBLIC_STORYBLOK_PREVIEW_TOKEN,
  use: [apiPlugin],
  apiOptions: {
    maxRetries: 0,
  },
  components: {
    avatarGroup: dynamic(() =>
      import('../components/elements/atoms/SBAvatarGroup/SBAvatarGroup').then(
        mod => mod.SBAvatarGroup,
      ),
    ),
    cta: dynamic(() => import('../components/elements/atoms/SBCta/SBCta').then(mod => mod.SBCta)),
    counter: dynamic(() =>
      import('../components/elements/atoms/SBCounter/SBCounter').then(mod => mod.SBCounter),
    ),
    image: dynamic(() =>
      import('../components/elements/atoms/SBImage/SBImage').then(mod => mod.SBImage),
    ),
    logo: dynamic(() =>
      import('../components/elements/atoms/SBLogo/SBLogo').then(mod => mod.SBLogo),
    ),
    quaterCircleWithText: dynamic(() =>
      import('../components/elements/atoms/SBQuaterCircleWithText/SBQuaterCircleWithText').then(
        mod => mod.SBQuaterCircleWithText,
      ),
    ),
    richtext: dynamic(() =>
      import('../components/elements/atoms/SBRichtext/SBRichtext').then(mod => mod.SBRichtext),
    ),
    separator: dynamic(() =>
      import('../components/elements/atoms/SBSeparator/SBSeparator').then(mod => mod.SBSeparator),
    ),
    typography: dynamic(() =>
      import('../components/elements/atoms/SBTypography/SBTypography').then(
        mod => mod.SBTypography,
      ),
    ),
    video: dynamic(() =>
      import('../components/elements/atoms/SBVideo/SBVideo').then(mod => mod.SBVideo),
    ),
    aboutServiceCard: dynamic(() =>
      import('../components/elements/cards/SBAboutServiceCard').then(mod => mod.SBAboutServiceCard),
    ),
    backgroundCard: dynamic(() =>
      import('../components/elements/cards/SBBackgroundCard').then(mod => mod.SBBackgroundCard),
    ),
    cardTestimonial: dynamic(() =>
      import('../components/elements/cards/SBCardTestimonial').then(mod => mod.SBCardTestimonial),
    ),
    caseStudyCard: dynamic(() =>
      import('../components/elements/cards/SBCaseStudyCard/SBCaseStudyCard').then(
        mod => mod.SBCaseStudyCard,
      ),
    ),
    personCard: dynamic(() =>
      import('../components/elements/cards/SBPersonCard').then(mod => mod.SBPersonCard),
    ),
    technologyCard: dynamic(() =>
      import('../components/elements/cards/SBTechnologyCard').then(mod => mod.SBTechnologyCard),
    ),
    verticallySeparatedCard: dynamic(() =>
      import('../components/elements/cards/SBVerticallySeparatedCard').then(
        mod => mod.SBVerticallySeparatedCard,
      ),
    ),
    animationWrapper: dynamic(() =>
      import('../components/elements/layout/SBAnimationWrapper/SBAnimationWrapper').then(
        mod => mod.SBAnimationWrapper,
      ),
    ),
    column: dynamic(() =>
      import('../components/elements/layout/SBColumn/SBColumn').then(mod => mod.SBColumn),
    ),
    container: dynamic(() =>
      import('../components/elements/layout/SBContainer/SBContainer').then(mod => mod.SBContainer),
    ),
    containerWithImage: dynamic(() =>
      import('../components/elements/layout/SBContainerWithImage/SBContainerWithImage').then(
        mod => mod.SBContainerWithImage,
      ),
    ),
    decoratorWrapper: dynamic(() =>
      import('../components/elements/layout/SBDecoratorWrapper/SBDecoratorWrapper').then(
        mod => mod.SBDecoratorWrapper,
      ),
    ),
    error: dynamic(() =>
      import('../components/elements/layout/SBError/SBError').then(mod => mod.SBError),
    ),
    grid: dynamic(() =>
      import('../components/elements/layout/SBGrid/SBGrid').then(mod => mod.SBGrid),
    ),
    megaMenuList: dynamic(() =>
      import('../components/elements/layout/navigation/SBMegaMenuList').then(
        mod => mod.SBMegaMenuList,
      ),
    ),
    navigation: dynamic(() =>
      import('../components/elements/layout/navigation/SBNavigation').then(mod => mod.SBNavigation),
    ),
    navigationMegaMenuItem: dynamic(() =>
      import('../components/elements/layout/navigation/SBNavigationMegaMenuItem').then(
        mod => mod.SBNavigationMegaMenuItem,
      ),
    ),
    row: dynamic(() => import('../components/elements/layout/SBRow/SBRow').then(mod => mod.SBRow)),
    table: dynamic(() =>
      import('../components/elements/layout/SBTable/SBTable').then(mod => mod.SBTable),
    ),
    tableRow: dynamic(() =>
      import('../components/elements/layout/SBTableRow/SBTableRow').then(mod => mod.SBTableRow),
    ),
    accordion: dynamic(() =>
      import('../components/elements/sections/accordion/SBAccordion').then(mod => mod.SBAccordion),
    ),
    accordionItem: dynamic(() =>
      import('../components/elements/sections/accordion/SBAccordionItem').then(
        mod => mod.SBAccordionItem,
      ),
    ),
    banner: dynamic(() =>
      import('../components/elements/sections/SBBanner/SBBanner').then(mod => mod.SBBanner),
    ),
    beforeAfter: dynamic(() =>
      import('../components/elements/sections/SBBeforeAfter/SBBeforeAfter').then(
        mod => mod.SBBeforeAfter,
      ),
    ),
    benefitItem: dynamic(() =>
      import('../components/elements/sections/SBBenefitsSection/SBBenefitItem').then(
        mod => mod.SBBenefitItem,
      ),
    ),
    benefitsSection: dynamic(() =>
      import('../components/elements/sections/SBBenefitsSection/SBBenefitsSection').then(
        mod => mod.SBBenefitsSection,
      ),
    ),
    breadcrumbs: dynamic(() =>
      import('../components/elements/sections/SBBreadcrumbs/SBBreadcrumbs').then(
        mod => mod.SBBreadcrumbs,
      ),
    ),
    carousel: dynamic(() =>
      import('../components/elements/sections/carousel/SBCarousel').then(mod => mod.SBCarousel),
    ),
    carouselTestimonial: dynamic(() =>
      import('../components/elements/sections/carousel/SBCarouselTestimonial').then(
        mod => mod.SBCarouselTestimonial,
      ),
    ),
    carouselWithCounters: dynamic(() =>
      import('../components/elements/sections/carousel/SBCarouselWithCounters').then(
        mod => mod.SBCarouselWithCounters,
      ),
    ),
    caseStudiesGrid: dynamic(() =>
      import('../components/elements/sections/SBCaseStudiesGrid/SBCaseStudiesGrid').then(
        mod => mod.SBCaseStudiesGrid,
      ),
    ),
    contactInfo: dynamic(() =>
      import('../components/elements/sections/SBContactInfo/SBContactInfo').then(
        mod => mod.SBContactInfo,
      ),
    ),
    coreWebVitals: dynamic(() =>
      import('../components/elements/sections/SBCoreWebVitals/SBCoreWebVitals').then(
        mod => mod.SBCoreWebVitals,
      ),
    ),
    doWeFitTogetherSection: dynamic(() =>
      import(
        '../components/elements/sections/SBDoWeFitTogetherSection/SBDoWeFitTogetherSection'
      ).then(mod => mod.SBDoWeFitTogetherSection),
    ),
    drivenByInnovationSection: dynamic(() =>
      import(
        '../components/elements/sections/SBDrivenByInnovationSection/SBDrivenByInnovationSection'
      ).then(mod => mod.SBDrivenByInnovationSection),
    ),
    eWebinarForm: dynamic(() =>
      import('../components/elements/sections/forms/SBEWebinarForm/SBEWebinarForm').then(
        mod => mod.SBEWebinarForm,
      ),
    ),
    getAnEstimateForm: dynamic(() =>
      import('../components/elements/sections/forms/SBGetAnEstimateForm/SBGetAnEstimateForm').then(
        mod => mod.SBGetAnEstimateForm,
      ),
    ),
    guaranteeSection: dynamic(() =>
      import('../components/elements/sections/SBGuaranteeSection/SBGuaranteeSection').then(
        mod => mod.SBGuaranteeSection,
      ),
    ),
    howWeSupportYouPattern: dynamic(() =>
      import(
        '../components/elements/sections/SBHowWeSupportYouPattern/SBHowWeSupportYouPattern'
      ).then(mod => mod.SBHowWeSupportYouPattern),
    ),
    jobOfferCards: dynamic(() =>
      import('../components/elements/sections/SBJobOfferCards/SBJobOfferCards').then(
        mod => mod.SBJobOfferCards,
      ),
    ),
    marquee: dynamic(() =>
      import('../components/elements/sections/SBMarquee/SBMarquee').then(mod => mod.SBMarquee),
    ),
    openingsBannerSection: dynamic(() =>
      import(
        '../components/elements/sections/SBOpeningsBannerSection/SBOpeningsBannerSection'
      ).then(mod => mod.SBOpeningsBannerSection),
    ),
    ourGuaranteeSection: dynamic(() =>
      import('../components/elements/sections/SBOurGuaranteeSection/SBOurGuaranteeSection').then(
        mod => mod.SBOurGuaranteeSection,
      ),
    ),
    ourServicesItem: dynamic(() =>
      import('../components/elements/sections/SBOurServicesItem/SBOurServicesItem').then(
        mod => mod.SBOurServicesItem,
      ),
    ),
    ourServicesSection: dynamic(() =>
      import('../components/elements/sections/SBOurServicesSection/SBOurServicesSection').then(
        mod => mod.SBOurServicesSection,
      ),
    ),
    quote: dynamic(() =>
      import('../components/elements/sections/SBQuote/SBQuote').then(mod => mod.SBQuote),
    ),
    recruitmentProcess: dynamic(() =>
      import('../components/elements/sections/SBRecruitmentProcess/SBRecruitmentProcess').then(
        mod => mod.SBRecruitmentProcess,
      ),
    ),
    serviceCardItem: dynamic(() =>
      import('../components/elements/sections/SBServicesList/SBServiceCardItem').then(
        mod => mod.SBServiceCardItem,
      ),
    ),
    servicesList: dynamic(() =>
      import('../components/elements/sections/SBServicesList/SBServicesList').then(
        mod => mod.SBServicesList,
      ),
    ),
    successState: dynamic(() =>
      import('../components/elements/sections/forms/SBSuccessState/SBSuccessState').then(
        mod => mod.SBSuccessState,
      ),
    ),
    supportItem: dynamic(() =>
      import('../components/elements/sections/SBSupportItem/SBSupportItem').then(
        mod => mod.SBSupportItem,
      ),
    ),
    supportSection: dynamic(() =>
      import('../components/elements/sections/SBSupportSection/SBSupportSection').then(
        mod => mod.SBSupportSection,
      ),
    ),
    tabs: dynamic(() =>
      import('../components/elements/sections/SBTabs/SBTabs').then(mod => mod.SBTabs),
    ),
    tableSection: dynamic(() =>
      import('../components/elements/sections/SBTableSection/SBTableSection').then(
        mod => mod.SBTableSection,
      ),
    ),
    teaser: dynamic(() =>
      import('../components/elements/sections/SBTeaser/SBTeaser').then(mod => mod.SBTeaser),
    ),
    testimonial: dynamic(() =>
      import('../components/elements/sections/SBTestimonial/SBTestimonial').then(
        mod => mod.SBTestimonial,
      ),
    ),
    page: dynamic(() => import('../components/contentTypes/SBPage').then(mod => mod.SBPage)),
    footer: dynamic(() => import('../components/contentTypes/SBFooter').then(mod => mod.SBFooter)),
    header: dynamic(() => import('../components/contentTypes/SBHeader').then(mod => mod.SBHeader)),
    preHeader: dynamic(() =>
      import('../components/contentTypes/SBPreHeader/SBPreHeader').then(mod => mod.SBPreHeader),
    ),
    careerOffer: dynamic(() =>
      import('../components/contentTypes/SBCareerOffer').then(mod => mod.SBCareerOffer),
    ),

    categoriesBar: dynamic(() =>
      import('../blog/src/components/elements/SBCategoriesBar/SBCategoriesBar').then(
        mod => mod.SBCategoriesBar,
      ),
    ),
    blogArticleCard: dynamic(() =>
      import('../blog/src/components/elements/SBArticleCard/SBArticleCard').then(
        mod => mod.SBArticleCard,
      ),
    ),
    blogArticlesByFilters: dynamic(() =>
      import('../blog/src/components/elements/SBArticlesByFilters/SBArticlesByFilters').then(
        mod => mod.SBArticlesByFilters,
      ),
    ),

    // content types
    article: dynamic(() =>
      import('../blog/src/components/contentTypes/SBArticle/SBArticle').then(mod => mod.SBArticle),
    ),
    articleAuthor: dynamic(() =>
      import('../blog/src/components/contentTypes/SBArticleAuthor/SBArticleAuthor').then(
        mod => mod.SBArticleAuthor,
      ),
    ),
    articleCategory: dynamic(() =>
      import('../blog/src/components/contentTypes/SBArticleCategory/SBArticleCategory').then(
        mod => mod.SBArticleCategory,
      ),
    ),
    articleShowcase: dynamic(() =>
      import('../blog/src/components/elements/SBArticleShowcase/SBArticleShowcase').then(
        mod => mod.SBArticleShowcase,
      ),
    ),
    collectionPageJsonLd: dynamic(() =>
      import('../seo/src/components/SBCollectionPageJsonLd/SBCollectionPageJsonLd').then(
        mod => mod.SBCollectionPageJsonLd,
      ),
    ),
    webpageJsonLd: dynamic(() =>
      import('../seo/src/components/SBWebpageJsonLd/SBWebpageJsonLd').then(
        mod => mod.SBWebpageJsonLd,
      ),
    ),
  },
});

export const api = storyblokInit({
  accessToken: env.NEXT_PUBLIC_STORYBLOK_PREVIEW_TOKEN,
  use: [apiPlugin],
});

interface GetStoryblokSdkInput {
  draftMode: boolean;
}

export const getStoryblokSdk = ({ draftMode }: GetStoryblokSdkInput): Sdk => {
  const isDraftModeEnabled = isDraftMode(draftMode);

  const wrapper: SdkFunctionWrapper = action => {
    const options: ISbStoriesParams = {
      version: isDraftModeEnabled ? 'draft' : 'published',
      resolve_relations: relations,
    };

    return action(options);
  };

  return getSdk(api, wrapper);
};

export * from './relations';
export * from './tags';
